// Cookie consent, as reported by the CookieYes consent manager. Mirrors the
// marketing site's src/lib/consent.ts (landscope-home), so a choice made on
// either property means the same thing on both (CookieYes shares it across
// landscope.earth subdomains).
//
// Posture (COOKIE_COMPLIANCE_TODO.md, Decision 3):
//   - No CookieYes tag on the page (local dev): everything is granted.
//   - Tag present but its API not loaded yet: denied until CookieYes reports.
//   - API loaded: exactly what the visitor chose.
//
// No runtime imports and no DOM access at module load, so node --test can
// load the decision functions directly.
export const CONSENT_MANAGER_ELEMENT_ID = 'cookieyes';
// The class CookieYes listens for (a delegated body click) to reopen its
// preference centre.
export const CONSENT_PREFERENCES_CLASS = 'cky-banner-element';
export const CookieYesCategory = {
    Necessary: 'necessary',
    Functional: 'functional',
    Analytics: 'analytics',
    Performance: 'performance',
    Advertisement: 'advertisement',
};
export const ConsentEvent = {
    BannerLoad: 'cookieyes_banner_load',
    ConsentUpdate: 'cookieyes_consent_update',
};
export const ALL_GRANTED = { statistics: true, advertisement: true };
export const ALL_DENIED = { statistics: false, advertisement: false };
export const decideConsent = (snapshot) => {
    if (snapshot.categories) {
        const c = snapshot.categories;
        return {
            statistics: c[CookieYesCategory.Analytics] === true || c[CookieYesCategory.Performance] === true,
            advertisement: c[CookieYesCategory.Advertisement] === true,
        };
    }
    return snapshot.cmpPresent ? ALL_DENIED : ALL_GRANTED;
};
// cookieyes_consent_update carries { accepted: string[], rejected: string[] }.
export const categoriesFromAccepted = (accepted) => {
    const categories = {};
    for (const slug of accepted) {
        categories[slug] = true;
    }
    return categories;
};
const readCookieYesCategories = () => {
    var _a, _b;
    const getConsent = window.getCkyConsent;
    if (typeof getConsent !== 'function') {
        return null;
    }
    return (_b = (_a = getConsent()) === null || _a === void 0 ? void 0 : _a.categories) !== null && _b !== void 0 ? _b : null;
};
export const isConsentManagerPresent = () => document.getElementById(CONSENT_MANAGER_ELEMENT_ID) !== null;
const readSnapshot = (categories) => ({
    cmpPresent: isConsentManagerPresent(),
    categories,
});
export const getConsentState = () => decideConsent(readSnapshot(readCookieYesCategories()));
export const hasStatisticsConsent = () => getConsentState().statistics;
export const hasAdvertisementConsent = () => getConsentState().advertisement;
const categoriesFromEvent = (event) => {
    const detail = event.detail;
    if (event.type === ConsentEvent.ConsentUpdate && Array.isArray(detail === null || detail === void 0 ? void 0 : detail.accepted)) {
        return categoriesFromAccepted(detail.accepted.filter((s) => typeof s === 'string'));
    }
    return readCookieYesCategories();
};
// Calls cb with the consent state every time CookieYes loads its state or the
// visitor changes their choice. Returns an unsubscribe function.
export const onConsentChange = (cb) => {
    const handler = (event) => {
        cb(decideConsent(readSnapshot(categoriesFromEvent(event))));
    };
    document.addEventListener(ConsentEvent.BannerLoad, handler);
    document.addEventListener(ConsentEvent.ConsentUpdate, handler);
    return () => {
        document.removeEventListener(ConsentEvent.BannerLoad, handler);
        document.removeEventListener(ConsentEvent.ConsentUpdate, handler);
    };
};
// Runs cb now if Advertisement consent is already granted, and again each time
// CookieYes reports it granted (a late "Accept", or accepting again after a
// withdrawal). cb must be idempotent.
export const onAdvertisementConsent = (cb) => {
    if (hasAdvertisementConsent()) {
        cb();
    }
    onConsentChange((state) => {
        if (state.advertisement) {
            cb();
        }
    });
};
